import { forwardRef } from '@chakra-ui/react-v2';
import { cx } from '@chakra-ui/utils';
import { memo, useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/legacy-components';

import { useTableStyles } from '../TableStylesContext';
import { useTableContext } from '../utils/context';
import { getClassName } from './className';

const RELAXED_PADDING_BY_SIZE: Record<string, number> = {
  sm: 2.25,
  md: 2.5,
};

export type TableHeaderProps = BoxProps;

/**
 * A Table subcomponent used to contain elements above the Table's grid.
 * - Place within the Table before the TableContent and TableFooter.
 * - Wraps a Chakra-UI Box and responds to Table's size.
 */
export const TableHeader = memo(
  forwardRef<TableHeaderProps, 'div'>(
    ({ children, className, ...restProps }, ref) => {
      const { header: headerStyles } = useTableStyles() ?? {};

      const { density, size } = useTableContext() ?? {};

      const headerCss = useMemo(
        () => ({
          ...headerStyles,
          ...(density === 'relaxed'
            ? {
                px: RELAXED_PADDING_BY_SIZE[size],
              }
            : null),
        }),
        [headerStyles, density, size],
      );

      return (
        <Box
          ref={ref}
          __css={headerCss}
          className={cx(getClassName('header'), className)}
          {...restProps}
        >
          {children}
        </Box>
      );
    },
  ),
);
TableHeader.displayName = 'MemoizedTableHeader';
